import store from '../../../src/js/store';
import isTouchDevice from './isTouchDevice.js';
export function calculateSelectionToolPosition(rects, iframeRect = null) {
    const selectionHeight = rects[rects.length - 1].bottom - rects[0].top;
    const firstRectInSelection = rects[0];
    // 48px is the air space between the selection and the tool in mobile devices to give space to the user finger
    const adjustmentTopPosition = isTouchDevice ? 48 : 20;
    const adjustmentLeftPosition = 200;

    // Rendered height of the collapsed toolbar box (min-h-12 = 48px + py-2 = 16px + border).
    // The color/AI/citation submenus open as separate absolutely-positioned dropdowns that flip
    // direction via `submenuDisplayInDown`, so they do not grow this box.
    const TOOLBAR_HEIGHT = 64;
    const SCREEN_MARGIN = 8;
    // The toolbar root carries a -mt-4 (16px negative margin), so it renders 16px above `top`.
    const TOOLBAR_MARGIN_TOP = 16;
    const viewportHeight = store.state.device.height;

    // Selection edges in viewport coordinates (EPUB rects are relative to the iframe, so add its offset).
    const selectionTop = iframeRect ? firstRectInSelection.top + iframeRect.top : firstRectInSelection.top;
    const selectionBottom = selectionTop + selectionHeight;

    // Position the selection tools just below the selected range to avoid overlap with contextual menus.
    // This is important for certain browsers and operating systems (e.g., iOS or Microsoft Edge),
    // where placing tools too close to the selection may cause display issues.
    let top = selectionBottom + adjustmentTopPosition;

    // Flip the toolbar above the selection when there is not enough room below, so it stays fully
    // visible and tappable near the bottom edge instead of being clipped against it.
    if (top + TOOLBAR_HEIGHT + SCREEN_MARGIN > viewportHeight) {
        top = selectionTop - adjustmentTopPosition - TOOLBAR_HEIGHT;
    }

    // Final safety clamp so the toolbar never leaves the viewport, regardless of the selection
    // position. The lower bound accounts for the negative top margin so the box does not render past
    // the top edge once the margin shifts it up.
    top = Math.max(SCREEN_MARGIN + TOOLBAR_MARGIN_TOP, Math.min(top, viewportHeight - TOOLBAR_HEIGHT - SCREEN_MARGIN));

    let left = firstRectInSelection.x + (firstRectInSelection.width / 3) * 2;

    if (iframeRect) {
        // Fix left position when the iframe is present(epub)
        left = firstRectInSelection.x + iframeRect.x + adjustmentLeftPosition;
    }

    // 405px is the total width of the selection tools with all items enabled, used to prevent the tools from displaying outside the window.
    // We need to improve this calculation and make it dynamic. First, we need to set a static size or render the component
    // before setting the position.
    const toolWidth = iframeRect ? 335 : 405;
    while (left + toolWidth > store.state.device.width) {
        left -= 10;
    }

    if (left < 200) {
        left = 200;
    }

    return { top: `${top}px`, left: `${left}px` };
}

/**
 * Computes the top and left anchor of the selection in viewport coordinates, used to place the
 * left-docked selection panel next to the selected text.
 * @param {DOMRect[]} rects - Array of DOMRect objects from the selection
 * @param {DOMRect|null} containerRect - Bounding rect of the container (iframe for EPUB, page for PDF)
 * @param {boolean} rectsRelativeToContainer - Whether rects are relative to container (true for EPUB iframe)
 * @returns {{selectionTop: number, selectionLeft: number}}
 */
export function calculateSelectionAnchor(rects, containerRect = null, rectsRelativeToContainer = false) {
    const firstRect = rects[0];

    const selectionTop = containerRect && rectsRelativeToContainer ? firstRect.top + containerRect.top : firstRect.top;

    // Left edge of the selected text in viewport coordinates. The leftmost rect approximates the
    // text column's left edge for multi-line selections; for a single line it is the selection's
    // start. Used to dock the panel just to the left of the text instead of at the viewport edge.
    let selectionLeft = Math.min(...Array.from(rects, rect => rect.x));
    if (containerRect && rectsRelativeToContainer) {
        selectionLeft += containerRect.x;
    }

    return { selectionTop, selectionLeft };
}

/**
 * Computes the fixed-position placement of a left-docked selection panel (notes, flashcards,
 * integrations). The panel is anchored just to the left of the selection with a small gap and
 * vertically aligned to the selection, falling back to the bottom of the viewport when the
 * selection sits low enough that the panel would overflow.
 * @param {Object} params
 * @param {number} params.selectionTop - Viewport Y of the selection's top edge
 * @param {number} params.selectionLeft - Viewport X of the selection's left edge
 * @param {number} params.viewportHeight - Viewport height in pixels
 * @param {number} params.panelWidth - Panel width in pixels
 * @param {number} [params.panelMaxHeight] - Max panel height in pixels (defaults to half the viewport)
 * @param {boolean} [params.allowBottomReposition=true] - Whether the panel may dock to the bottom when the selection is low
 * @returns {{position: string, left: string, top?: string, bottom?: string}}
 */
export function calculateLeftPanelPlacement({
    selectionTop,
    selectionLeft,
    viewportHeight,
    panelWidth,
    panelMaxHeight = viewportHeight / 2,
    allowBottomReposition = true,
}) {
    const GAP_FROM_TEXT = 16;
    const SCREEN_MARGIN = 16;

    return {
        position: 'fixed',
        left: `${Math.max(SCREEN_MARGIN, selectionLeft - GAP_FROM_TEXT - panelWidth)}px`,
        ...resolvePanelVerticalPlacement({
            anchorTop: selectionTop,
            viewportHeight,
            panelMaxHeight,
            allowBottomReposition,
        }),
    };
}

/**
 * Computes the vertical placement shared by the side-docked panels: aligned to
 * the anchor's top, falling back to the bottom of the viewport when the anchor
 * sits low enough that the panel would overflow.
 * @param {Object} params
 * @param {number} params.anchorTop - Viewport Y of the anchor's top edge
 * @param {number} params.viewportHeight - Viewport height in pixels
 * @param {number} params.panelMaxHeight - Max panel height in pixels
 * @param {boolean} params.allowBottomReposition - Whether the panel may dock to the bottom when the anchor is low
 * @returns {{top?: string, bottom?: string}}
 */
function resolvePanelVerticalPlacement({ anchorTop, viewportHeight, panelMaxHeight, allowBottomReposition }) {
    const BOTTOM_MARGIN = 24;
    const TOP_MARGIN = 80;

    const isAnchorBelowMiddle = anchorTop > viewportHeight / 2;
    const remainingSpaceBelowAnchor = viewportHeight - anchorTop;
    if (allowBottomReposition && isAnchorBelowMiddle && remainingSpaceBelowAnchor < panelMaxHeight) {
        return { bottom: `${BOTTOM_MARGIN}px` };
    }
    return { top: `${Math.max(TOP_MARGIN, Math.min(anchorTop, viewportHeight - panelMaxHeight))}px` };
}
